import styled from 'styled-components';
import { forwardRef, useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { Controller } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';

import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { RadioLabel, RadioOptionWrapper, RadioBlockGroup } from 'design-system/src/components/Radio/Radio';
import { Checkbox, CheckboxProps } from 'design-system/src/components/Checkbox/Checkbox';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage.tsx';
import { OtherRequestButton } from '../actions/shared/OtherRequestButton';

import {
  client,
  Drug,
  Prescriber,
  TremfyaExpressEnrollmentFormData,
  TremfyaExpressEnrollmentFormDataDefaultValues,
  TherapyTask,
} from '../../../api';
import { TremfyaEnrollmentPatientModal } from './TremfyaEnrollmentPatientModal.tsx';
import { TremfyaEnrollmentSavingsProgramSection } from './TremfyaEnrollmentSavingsProgramSection.tsx';
import { convertYesNoToBoolean, convertBooleanToYesNo, requiredErrorMessage, formatAddress } from '../../../utils.tsx';
import { formatNameReversed } from 'shared-utils/utils/formatString';
import { CardSection, CustomHairline } from './sharedStyles.tsx';
import { therapyIndicationValues, TherapyIndication } from './dropdownValues.ts';
import { CardContainer, CardLabelText, PatientPrescriberInfoCards } from './PatientPrescriberInfoCards.tsx';
import { formatPhoneNumber } from 'shared-utils/utils/formatPhoneNumber';

const OuterForm = styled.form`
  margin-top: 16px;
`;

const HiddenFieldset = styled.fieldset`
  all: unset;
  display: block;
`;

const InnerCard = styled(CardContainer)`
  display: flex;
  padding: 16px;
  gap: 16px;
  flex-direction: column;
`;

const SubmitErrorMessageContainer = styled(CardSection)`
  margin-top: 0;
  display: flex;
  align-items: flex-end;
`;

const InlineButton = styled(Button)`
  display: inline-block;
`;

const SmallPrintSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 24px 12px 24px;
`;

const SubmitButtonContainer = styled.div`
  padding: 24px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

const ForwardedCheckbox = forwardRef<HTMLButtonElement, CheckboxProps>((props, ref) => (
  <Checkbox {...props} ref={ref} />
));
const StyledCheckbox = styled(ForwardedCheckbox)`
  margin: -9px 0 0 -9px !important;
  padding-right: 12px !important;
`;

// Patient fields that need validation
const PATIENT_FIELDS = [
  'patient_first_name',
  'patient_last_name',
  'patient_dob',
  'patient_sex',
  'patient_phone',
  'patient_phone_type',
  'patient_email',
  'patient_address',
  'patient_city',
  'patient_state',
  'patient_zip',
] as const;

interface TremfyaExpressEnrollmentCoreFormProps {
  drug: Drug;
  prescriber?: Prescriber;
  formData: TremfyaExpressEnrollmentFormData;
  declineButton?: React.ReactNode;
  setShowNewFollowupTaskModal?: (show: boolean) => void;
  onSuccess: () => void;
  setIsSubmitting?: (isSubmitting: boolean) => void;
  submissionEndpoint: string;
  isCompleted: boolean;
}

/**
 * Core of the Tremfya Express Enrollment task form, used for both logged in and logged out views.
 */
const TremfyaExpressEnrollmentCoreForm: React.FC<TremfyaExpressEnrollmentCoreFormProps> = ({
  prescriber,
  formData,
  declineButton,
  setShowNewFollowupTaskModal,
  onSuccess,
  setIsSubmitting,
  submissionEndpoint,
  isCompleted,
}) => {
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const submit = useMutation<void, Error, TremfyaExpressEnrollmentFormDataDefaultValues>({
    mutationFn: async (data) => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "submit Tremfya express enrollment" for task`);
        return;
      }
      setIsSubmitting && setIsSubmitting(true);

      const formSubmissionData: Record<string, string | boolean | null> = {};

      const dataKeys = Object.keys(data);
      dataKeys.forEach((key) => {
        if (['has_existing_card', 'is_eligible'].includes(key)) {
          // Force string values back to boolean
          const value = data[key as keyof TremfyaExpressEnrollmentFormDataDefaultValues];
          formSubmissionData[key] = convertYesNoToBoolean(value as 'Yes' | 'No' | null);
        } else {
          formSubmissionData[key] = data[key as keyof TremfyaExpressEnrollmentFormDataDefaultValues];
        }
      });

      // some sections are hidden based on some answers, so the answers to the question in those sections
      // are not included in the form submission
      if (formSubmissionData.therapy_task !== TherapyTask.SavingsProgram) {
        formSubmissionData.is_eligible = null;
        if (
          ![TherapyIndication.ActivePsoriaticArthritis, TherapyIndication.ModerateToSeverePlaquePsoriasis].includes(
            formSubmissionData.therapy_indication as TherapyIndication,
          )
        ) {
          formSubmissionData.has_existing_card = null;
        }
      }
      if (!formSubmissionData.is_eligible) {
        formSubmissionData.patient_authorization = null;
        formSubmissionData.requirements_provided = null;
      }

      const res = await client.post(submissionEndpoint, {
        form_data: formSubmissionData,
      });
      return res.data;
    },
    onSuccess: () => {
      onSuccess();
      setIsSubmitting && setIsSubmitting(false);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
      setIsSubmitting && setIsSubmitting(false);
    },
  });

  const { handleSubmit, control, formState, trigger, getValues } =
    useForm<TremfyaExpressEnrollmentFormDataDefaultValues>({
      defaultValues: {
        ...formData,
        has_existing_card: convertBooleanToYesNo(formData.has_existing_card),
        is_eligible: convertBooleanToYesNo(formData.is_eligible),
      },
      mode: 'onChange',
    });
  const { errors } = formState;

  const [therapyTaskValue, therapyIndicationValue, isEligibleValue, includeNurseGuideValue] = useWatch({
    control,
    name: ['therapy_task', 'therapy_indication', 'is_eligible', 'include_nurse_guide'],
  });

  const [
    patientFirstName,
    patientLastName,
    patientDob,
    patientSex,
    patientPhone,
    patientPhoneType,
    patientEmail,
    patientAddress,
    patientCity,
    patientState,
    patientZip,
  ] = useWatch({
    control,
    name: PATIENT_FIELDS,
  });

  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  // Clear validation errors when user opens the patient modal to edit
  const handleOpenPatientModal = () => {
    setHasValidationErrors(false);
    setIsPatientModalOpen(true);
  };

  const patientName = formatNameReversed(patientFirstName, patientLastName);
  const patientPhoneLine = patientPhone ? formatPhoneNumber(patientPhone) : '';
  const patientEmailLine = patientEmail || '';
  const patientAddressLine = formatAddress(patientAddress, patientCity, patientState, patientZip);

  const requiredFields = [
    patientFirstName,
    patientLastName,
    patientDob,
    patientSex,
    patientPhone,
    patientPhoneType,
    patientAddress,
    patientCity,
    patientState,
    patientZip,
  ];
  if (therapyTaskValue === TherapyTask.NurseGuide) {
    requiredFields.push(patientEmail || '');
  }
  // check if any of the required field values are falsy
  const hasMissingPatientInfo = requiredFields.some((fieldValue) => !fieldValue);

  const privacyPolicyLink = (
    <InlineButton
      variant="link"
      href="https://innovativemedicine.jnj.com/us/privacy-policy"
      target="_blank"
      rel="noopener noreferrer"
    >
      Privacy Policy
    </InlineButton>
  );

  const therapyIndicationOptionId = useId();
  const includeNurseGuideCheckboxId = useId();
  const savingsProgramTherapyTaskOptionId = useId();
  const savingsProgramNurseGuideOptionId = useId();
  const savingsProgramHasExistingCardYesId = useId();
  const savingsProgramHasExistingCardNoId = useId();

  return (
    <>
      <PatientPrescriberInfoCards
        prescriber={prescriber}
        providerNpi={formData.provider_npi}
        providerPracticeName={formData.provider_practice_name}
        providerAddress={formData.provider_address}
        patientName={patientName}
        patientDob={patientDob}
        patientSex={patientSex}
        patientPhoneLine={patientPhoneLine}
        patientEmailLine={patientEmailLine}
        patientAddressLine={patientAddressLine}
        onEditPatient={handleOpenPatientModal}
        isEditDisabled={isCompleted}
        hasMissingPatientInfo={hasMissingPatientInfo}
      />

      <TremfyaEnrollmentPatientModal
        isOpen={isPatientModalOpen}
        onClose={() => setIsPatientModalOpen(false)}
        control={control}
        errors={errors}
        therapyTaskValue={therapyTaskValue as TherapyTask}
        isCompleted={isCompleted}
        trigger={trigger}
        patientFields={PATIENT_FIELDS}
      />

      <OuterForm
        onSubmit={handleSubmit(async (data) => {
          // we need to manually validate the patient fields because these are in a modal that's currently closed
          const currentValues = getValues();

          // Check required patient fields manually
          let requiredPatientFields = [...PATIENT_FIELDS];

          // Remove email if nurse guide is not selected
          if (therapyTaskValue !== TherapyTask.NurseGuide) {
            requiredPatientFields = PATIENT_FIELDS.filter((field) => field !== 'patient_email');
          }

          // We're manually checking for missing fields because the patient modal is currently closed, so the form
          // is not validating the patient fields properly
          const missingFields = requiredPatientFields.filter((field) => {
            const value = currentValues[field as keyof typeof currentValues];
            return !value || value === '';
          });

          if (missingFields.length === 0) {
            // Also run trigger validation for format validation (email, phone, etc.)
            const isFormatValid = await trigger(requiredPatientFields);

            if (isFormatValid) {
              setHasValidationErrors(false);
              submit.mutate(data);
            } else {
              setHasValidationErrors(true);
            }
          } else {
            setHasValidationErrors(true);
          }
        })}
      >
        {/* using a disabled fieldset lets us skip manually disabling all text inputs and radio buttons.
            the other form elements do still need to be manually disabled though */}
        <HiddenFieldset disabled={isCompleted}>
          <CardContainer $paddingBottom={isCompleted ? '32px' : '0'}>
            <CardSection>
              <CardLabelText renderedAs="h2">Confirm prescribed therapy</CardLabelText>
              <Typography styledAs="bodySmallDMSans">
                This form is intended to be used by a member of a patient's "Care Team", which includes individuals who
                provide care for a patient. The information you provide may be used by Johnson & Johnson Health Care
                Systems Inc., its affiliates and its service providers to enroll your patient in the Savings Program,
                initiate a benefits investigation and/or rebate request on your patient's behalf, or sign your patient
                up for other support options that may be available. In order to personalize the information the patient
                receives, the information provided may be combined with information from other sources, as described in
                our {privacyPolicyLink}. Our Privacy Policy further governs the use of the information you provide.
              </Typography>
            </CardSection>

            <CustomHairline />

            <CardSection>
              <CardLabelText renderedAs="h2">What support would you like for your patient?</CardLabelText>
              <Controller
                control={control}
                name="therapy_task"
                rules={{ required: true }}
                render={({ field }) => (
                  <RadioBlockGroup>
                    <RadioOptionWrapper
                      id={savingsProgramTherapyTaskOptionId}
                      name="therapy_task"
                      value={TherapyTask.SavingsProgram}
                      checked={field.value === TherapyTask.SavingsProgram}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        field.onChange(event.target.value);
                      }}
                      hasError={!!errors.therapy_task}
                    >
                      <RadioLabel>Savings Program + Nurse Guide (Commercially-insured patients only)*</RadioLabel>
                    </RadioOptionWrapper>
                    <RadioOptionWrapper
                      id={savingsProgramNurseGuideOptionId}
                      name="therapy_task"
                      value={TherapyTask.NurseGuide}
                      checked={field.value === TherapyTask.NurseGuide}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        field.onChange(event.target.value);
                      }}
                      hasError={!!errors.therapy_task}
                    >
                      <RadioLabel>Nurse Guide only (Available to all patients)</RadioLabel>
                    </RadioOptionWrapper>
                    {errors.therapy_task && requiredErrorMessage}
                  </RadioBlockGroup>
                )}
              />
            </CardSection>

            <SmallPrintSection>
              <Typography styledAs="bodyExtraSmallSpaceGrotesk" color="var(--grayscale-600)">
                *If the patient does not meet the requirements for the Savings Program, they will be enrolled in Nurse
                Guide support.
              </Typography>
            </SmallPrintSection>

            {therapyTaskValue && (
              <CardSection>
                <InnerCard>
                  {therapyTaskValue === TherapyTask.SavingsProgram && (
                    <TremfyaEnrollmentSavingsProgramSection
                      control={control}
                      errors={errors}
                      isEligibleValue={isEligibleValue}
                      isCompleted={isCompleted}
                    />
                  )}

                  {(isEligibleValue === 'Yes' || therapyTaskValue === TherapyTask.NurseGuide) && (
                    <>
                      <div>
                        <Typography styledAs="bodySmallSpaceGrotesk" renderedAs="h3" weight={500}>
                          TREMFYA withMe Nurse Guide***
                        </Typography>
                        <Typography styledAs="bodySmallSpaceGrotesk">
                          A dedicated Nurse Guide is here to help with free personalized 1-on-1 support over the phone
                          throughout your patient's treatment journey.
                        </Typography>
                      </div>

                      <Controller
                        control={control}
                        name="include_nurse_guide"
                        render={({ field }) => (
                          <CheckboxContainer>
                            <StyledCheckbox
                              {...field}
                              onChange={() => field.onChange(!field.value)}
                              // the user doesn't really have a choice if they've selected the nurse guide option; they're getting
                              // this by default, so we're checking the box for them
                              checked={!!field.value || therapyTaskValue === TherapyTask.NurseGuide}
                              disabled={therapyTaskValue === TherapyTask.NurseGuide || isCompleted}
                              id={includeNurseGuideCheckboxId}
                            />
                            <label htmlFor={includeNurseGuideCheckboxId}>
                              <Typography styledAs="bodySmallDMSans">
                                My patient would like to receive a call from a TREMFYA withMe Nurse Guide* in 1-2
                                business days to learn more about 1-on-1 support available throughout their treatment
                                journey. If the patient wants to talk to someone immediately, they should call{' '}
                                <strong>833-WITHME1 (833-948-4631).</strong> I represent that I have authorization from
                                the patient that complies with state and federal law, and permits me to provide their
                                information for this purpose.
                              </Typography>
                              {therapyTaskValue === TherapyTask.SavingsProgram && (
                                <CardLabelText weight={600}>
                                  You may opt the patient out from receiving this phone call by unchecking the box.
                                </CardLabelText>
                              )}
                            </label>
                          </CheckboxContainer>
                        )}
                      />
                    </>
                  )}
                </InnerCard>
              </CardSection>
            )}

            {(isEligibleValue === 'Yes' || therapyTaskValue === TherapyTask.NurseGuide) && (
              <>
                <CustomHairline />

                <CardSection>
                  <CardLabelText>Confirm condition</CardLabelText>
                  <Controller
                    control={control}
                    name="therapy_indication"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <RadioBlockGroup>
                        {therapyIndicationValues.map((value) => (
                          <RadioOptionWrapper
                            id={`${therapyIndicationOptionId}-${value}`}
                            key={value}
                            name="therapy_indication"
                            value={value}
                            checked={field.value === value}
                            onChange={() => field.onChange(value)}
                            hasError={!!errors.therapy_indication}
                          >
                            <RadioLabel>{value}</RadioLabel>
                          </RadioOptionWrapper>
                        ))}
                        {errors.therapy_indication && requiredErrorMessage}
                      </RadioBlockGroup>
                    )}
                  />
                </CardSection>

                {therapyTaskValue === TherapyTask.SavingsProgram &&
                  (therapyIndicationValue === TherapyIndication.ActivePsoriaticArthritis ||
                    therapyIndicationValue === TherapyIndication.ModerateToSeverePlaquePsoriasis) && (
                    <CardSection>
                      <InnerCard>
                        <div>
                          <CardLabelText>Card information</CardLabelText>
                          <Typography styledAs="bodyExtraSmallSpaceGrotesk">
                            Does the patient have a card already?
                          </Typography>
                        </div>
                        <Controller
                          control={control}
                          name="has_existing_card"
                          rules={{ required: true }}
                          render={({ field }) => (
                            <RadioBlockGroup>
                              <RadioOptionWrapper
                                {...field}
                                id={savingsProgramHasExistingCardYesId}
                                name="has_existing_card"
                                value="Yes"
                                checked={field.value === 'Yes'}
                                onChange={() => field.onChange('Yes')}
                                hasError={!!errors.has_existing_card}
                              >
                                <RadioLabel>Yes</RadioLabel>
                              </RadioOptionWrapper>
                              <RadioOptionWrapper
                                {...field}
                                id={savingsProgramHasExistingCardNoId}
                                name="has_existing_card"
                                value="No"
                                checked={field.value === 'No'}
                                onChange={() => field.onChange('No')}
                                hasError={!!errors.has_existing_card}
                              >
                                <RadioLabel>No</RadioLabel>
                              </RadioOptionWrapper>
                              {errors.has_existing_card && requiredErrorMessage}
                            </RadioBlockGroup>
                          )}
                        />
                      </InnerCard>
                    </CardSection>
                  )}
              </>
            )}

            <SmallPrintSection>
              <Typography styledAs="bodyExtraSmallSpaceGrotesk" color="var(--grayscale-600)">
                **The patient support and resources provided by Tremfya withMe are not intended to give medical advice,
                replace a treatment plan from the patient's healthcare provider, offer services that would normally be
                performed by the provider's office, or serve as a reason to prescribe TREMFYA®.
              </Typography>
              {(therapyTaskValue === TherapyTask.NurseGuide ||
                (isEligibleValue === 'Yes' && !!includeNurseGuideValue)) && (
                <Typography styledAs="bodyExtraSmallSpaceGrotesk" color="var(--grayscale-600)">
                  ***Nurse Guides do not provide medical advice.
                </Typography>
              )}
            </SmallPrintSection>

            {!isCompleted && (
              <>
                <SubmitButtonContainer>
                  <ButtonRow align={Alignment.SpaceBetween} fullWidth>
                    <ButtonRow align={Alignment.Left}>
                      {declineButton}
                      {setShowNewFollowupTaskModal && (
                        <OtherRequestButton setShowNewFollowupTaskModal={setShowNewFollowupTaskModal} />
                      )}
                    </ButtonRow>
                    <Button
                      type="submit"
                      size="sm"
                      width={!declineButton && !setShowNewFollowupTaskModal ? 'full' : 'hug'}
                      isLoading={submit.isPending}
                    >
                      {therapyTaskValue === TherapyTask.SavingsProgram && isEligibleValue === 'No'
                        ? 'Acknowledge and complete task'
                        : 'Submit'}
                    </Button>
                  </ButtonRow>
                </SubmitButtonContainer>

                {(!!Object.keys(errors).length || hasValidationErrors) && (
                  <SubmitErrorMessageContainer>
                    <ErrorMessage marginTop="0">Please complete the required fields</ErrorMessage>
                  </SubmitErrorMessageContainer>
                )}
              </>
            )}
          </CardContainer>
        </HiddenFieldset>
      </OuterForm>
    </>
  );
};

export default TremfyaExpressEnrollmentCoreForm;
